import { logger } from '../../common/logger';
import React, {
  ErrorInfo,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { logAndClearErrorStack } from './windowOnError';
import { logUnexpectedError } from './errorUtils';

type Props = {
  errorBoundaryError?: Error;
  errorBoundaryErrorInfo?: ErrorInfo;
};

/**
 * Errors are caught and stored in this Provider. Error-related redirects are
 * handled in useAreenaRedirects. Errors and state fixing is handled in
 * ErrorConsumer.
 * @param children
 * @param errorBoundaryError
 * @param errorBoundaryErrorInfo
 */
export const ErrorProvider: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  errorBoundaryError,
  errorBoundaryErrorInfo,
}) => {
  const [errors, setErrors] = useState<Error[]>([]);
  const [currentError, setCurrentError] = useState<Error | null>(null);

  const addUniqueError = (error: Error) =>
    setErrors(oldErrors => {
      const errorText = errorToText(error);
      const errorsAsText = oldErrors.map(errorToText);

      if (errorsAsText.includes(errorText)) {
        return oldErrors;
      }

      return [error, ...oldErrors];
    });

  useEffect(() => {
    const latestError = errors[0];

    const isErrorBeingHandled = !!currentError;
    const hasErrors = !!latestError;

    if (hasErrors && !isErrorBeingHandled) {
      setCurrentError(latestError);
      setErrors(errors.slice(1));
    }
  }, [currentError, errors]);

  useEffect(() => {
    if (errorBoundaryError) {
      logger.debug('Error boundary error. Showing fallback component.');
      logUnexpectedError(errorBoundaryError);
    }
  }, [errorBoundaryError, errorBoundaryErrorInfo?.componentStack]);

  useEffect(() => {
    logAndClearErrorStack();

    window.onerror = (_message, _source, _lineNumber, _columnNumber, error) => {
      logger.debug({ error }, 'onerror listener');
      if (!error) return;
      addUniqueError(error);

      return true; // Should the default error behaviour of the browser be cancelled
    };

    window.onunhandledrejection = event => {
      logger.debug({ reason: event.reason }, 'onunhandledrejection listener');

      if (!(event.reason instanceof Error)) {
        logUnexpectedError(event.reason);
      }

      const error =
        event.reason instanceof Error ? event.reason : new Error(event.reason);

      addUniqueError(error);

      event.preventDefault();
    };
  }, []);

  const notifyErrorFromComponent = useCallback((error: Error) => {
    logger.debug('Error notified');
    addUniqueError(error);
  }, []);

  return (
    <ErrorContext.Provider
      value={{
        markErrorHandled: () => setCurrentError(null),
        errorToHandle: currentError,
        notifyErrorFromComponent,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

const errorToText = (error: Error) => `${error.name}:${error.message}`;

export function useErrorContext(): Value {
  return useContext(ErrorContext);
}

type Value = {
  markErrorHandled: () => void;
  errorToHandle: Error | null;
  notifyErrorFromComponent: (error: Error) => void;
};

const ErrorContext = React.createContext<Value>({
  markErrorHandled() {
    logger.error("Error context doesn't have a Provider");
  },
  errorToHandle: null,
  notifyErrorFromComponent() {
    logger.error("Error context doesn't have a Provider");
  },
});
